import React, { useCallback, useEffect, useState } from 'react';

import { Button, Paper, CircularProgress, Alert } from '@mui/material';
import confetti from 'canvas-confetti';

import { UploadType, v3 } from '@liscio/api';
import { useApiClient, useTrackEvent } from '@liscio/common';
import { Typography, Box, Stack, Icon } from '@liscio/ui';

import {
  AI_UPLOAD_ERROR,
  isAiUploadAllowed,
  SAMPLE_DOCUMENTS,
} from './constants';
import { FileAssociationPreview } from './FileAssociationPreview';
import { FileIntelligenceUploadZone } from './FileIntelligenceUploadZone';
import { useHomeData } from '../../../../fetch-utils/users/user-hooks';
import {
  ONBOARDING_STAGE_KEYS,
  useOnboardingJourneySteps,
} from '../../hooks/useOnboardingJourneySteps';
import {
  useAccountNumericId,
  useAccountStepSkipped,
  useContactNumericId,
  useOnboardingActions,
  useOnboardingError,
  useAiIngestionStatus,
} from '../../hooks/useOnboardingStore';

const AiIngestionStatus = {
  pending: 'pending',
  completed: 'completed',
  failed: 'failed',
} as const;

/**
 * The terminal result we actually care about — distinct from the raw ingestion
 * status. `completed` is split into `named` vs `noName` because an arbitrary
 * image can ingest "completed" yet yield no name to suggest.
 */
export const IngestionOutcome = {
  pending: 'pending',
  named: 'named',
  noName: 'no_name',
  failed: 'failed',
  unknown: 'unknown',
} as const;

type IngestionOutcomeValue =
  (typeof IngestionOutcome)[keyof typeof IngestionOutcome];

/** Pulls the bits we need out of the polled get_document_details response. */
export const parseDocDetails = (
  documentDetails: unknown
): {
  status?: string;
  suggestedName: string;
  originalName: string;
  documentUuid?: string;
} => {
  const responseData = documentDetails as {
    data?: {
      ai_ingestion_status?: string;
      suggested_name?: string | null;
      file_name?: string | null;
      doc_name?: string | null;
      uuid?: string;
    };
  };
  return {
    status: responseData?.data?.ai_ingestion_status,
    suggestedName: responseData?.data?.suggested_name?.trim() || '',
    originalName:
      responseData?.data?.file_name?.trim() ||
      responseData?.data?.doc_name?.trim() ||
      '',
    documentUuid: responseData?.data?.uuid,
  };
};

/** Maps a raw status + suggested name onto the outcome the UI reacts to. */
export const getIngestionOutcome = (
  status: string | undefined,
  suggestedName: string
): IngestionOutcomeValue => {
  switch (status) {
    case AiIngestionStatus.pending:
      return IngestionOutcome.pending;
    case AiIngestionStatus.completed:
      return suggestedName ? IngestionOutcome.named : IngestionOutcome.noName;
    case AiIngestionStatus.failed:
      return IngestionOutcome.failed;
    default:
      return IngestionOutcome.unknown;
  }
};

/** Showcase uploads: published to vault, firm-only (not client portal). */
const ONBOARDING_SHOWCASE_UPLOAD_OPTIONS = {
  is_draft: false,
  is_private_to_firm: true,
} as const;

export interface FileIntelligenceViewProps {}

export const FileIntelligenceView: React.FC<FileIntelligenceViewProps> = () => {
  const apiClient = useApiClient();
  const { data: homeData } = useHomeData();
  const { trackEvent } = useTrackEvent();
  const { recordOnboardingJourneyStep } = useOnboardingJourneySteps();

  const error = useOnboardingError();
  const aiIngestionStatus = useAiIngestionStatus();
  const contactNumericId = useContactNumericId();
  const accountNumericId = useAccountNumericId();
  const accountStepSkipped = useAccountStepSkipped();
  const { setHasUploadedFile, setAiIngestionStatus, setError } =
    useOnboardingActions();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState<number | null>(null);
  // null = not yet known; true = AI produced a name; false = ingestion finished
  // but there was nothing nameable (e.g. an arbitrary image). Distinct from
  // `aiIngestionStatus === 'completed'`, which only means the pipeline ran.
  const [hasNameSuggestion, setHasNameSuggestion] = useState<boolean | null>(
    null
  );
  const aiIngestionCompletedFiredRef = React.useRef(false);
  const aiIngestionNoNameFiredRef = React.useRef(false);
  const aiIngestionFailedFiredRef = React.useRef(false);

  const resetUploadState = useCallback(() => {
    setSelectedFile(null);
    setUploadSuccess(false);
    setUploadedFileId(null);
    setHasNameSuggestion(null);
    setHasUploadedFile(false);
    setAiIngestionStatus(AiIngestionStatus.pending);
    setError(null);
    setIsUploading(false);
    aiIngestionCompletedFiredRef.current = false;
    aiIngestionNoNameFiredRef.current = false;
    aiIngestionFailedFiredRef.current = false;
  }, [setAiIngestionStatus, setError, setHasUploadedFile]);

  // Clear the previous terminal result at the very start of a new upload.
  // Without this, the prior success/no-name/failed alert lingers through the
  // async upload window, so the first click after a terminal state looks like a
  // no-op. Resetting status to `pending` hides those alerts (all gated on a
  // terminal status), and clearing the ref lets a subsequent file fire its own
  // completion event. `hasNameSuggestion` is intentionally left as-is — it is
  // gated behind a terminal status, so the next poll recomputes it.
  const beginNewUpload = useCallback(() => {
    setUploadSuccess(false);
    setUploadedFileId(null);
    setHasUploadedFile(false);
    setAiIngestionStatus(AiIngestionStatus.pending);
    setError(null);
    aiIngestionCompletedFiredRef.current = false;
    aiIngestionNoNameFiredRef.current = false;
    aiIngestionFailedFiredRef.current = false;
  }, [setAiIngestionStatus, setError, setHasUploadedFile]);

  const { data: documentDetails } = v3.useGetApiV3GetDocumentDetailsId(
    uploadedFileId?.toString() || '',
    {},
    {
      query: {
        enabled: !!uploadedFileId,
        refetchInterval: (data: unknown) => {
          const { status } = parseDocDetails(data);
          if (
            status === AiIngestionStatus.completed ||
            status === AiIngestionStatus.failed
          ) {
            return false;
          }
          return 2000;
        },
      },
    }
  );

  useEffect(() => {
    const { status, suggestedName } = parseDocDetails(documentDetails);
    const outcome = getIngestionOutcome(status, suggestedName);
    if (outcome === IngestionOutcome.unknown) return;

    // Capture before mirroring: confetti only fires on the pending→completed
    // edge, so we need the prior status.
    const previousStatus = aiIngestionStatus;
    setAiIngestionStatus(status as 'pending' | 'completed' | 'failed');

    if (outcome !== IngestionOutcome.pending && uploadedFileId == null) {
      return;
    }

    switch (outcome) {
      case IngestionOutcome.named:
        setHasNameSuggestion(true);
        if (previousStatus === AiIngestionStatus.pending) {
          confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        }
        if (!aiIngestionCompletedFiredRef.current) {
          aiIngestionCompletedFiredRef.current = true;
          trackEvent('ai_ingestion_completed', {
            source: 'onboarding',
            onboarding_step: 'ai_ingestion_completed',
            user_role: 'firm_employee',
            company_name: homeData?.data?.userpilot_data?.firm_name,
          });
          void recordOnboardingJourneyStep({
            stageKey: ONBOARDING_STAGE_KEYS.aiIngestionCompleted,
            status: 'completed',
            metadata: {
              doc_id: uploadedFileId,
              outcome: 'named',
            },
          });
        }
        break;
      case IngestionOutcome.noName:
        // Ingested, but nothing to rename — keep "Complete setup" disabled and
        // surface the "try another file" prompt (same gate as a failure).
        setHasNameSuggestion(false);
        setHasUploadedFile(false);
        if (!aiIngestionNoNameFiredRef.current) {
          aiIngestionNoNameFiredRef.current = true;
          void recordOnboardingJourneyStep({
            stageKey: ONBOARDING_STAGE_KEYS.aiIngestionNoName,
            status: 'not_applicable',
            metadata: {
              doc_id: uploadedFileId,
              outcome: 'no_name',
            },
          });
        }
        break;
      case IngestionOutcome.failed:
        setHasNameSuggestion(null);
        setHasUploadedFile(false);
        if (!aiIngestionFailedFiredRef.current) {
          aiIngestionFailedFiredRef.current = true;
          void recordOnboardingJourneyStep({
            stageKey: ONBOARDING_STAGE_KEYS.aiIngestionFailed,
            status: 'stalled',
            metadata: {
              doc_id: uploadedFileId,
              outcome: 'failed',
            },
          });
        }
        break;
      default:
        // pending — status mirrored above; nothing else to do.
        break;
    }
  }, [
    aiIngestionStatus,
    documentDetails,
    setAiIngestionStatus,
    setHasUploadedFile,
    recordOnboardingJourneyStep,
    trackEvent,
    homeData,
    uploadedFileId,
  ]);

  const handleSampleFileClick = async (
    samplePath: string,
    filename: string
  ) => {
    try {
      beginNewUpload();
      setSelectedFile(
        new File([new Blob()], filename, { type: 'application/pdf' })
      );
      setIsUploading(true);

      const response = await fetch(`${window.location.origin}${samplePath}`, {
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const file = new File([blob], filename, { type: 'application/pdf' });

      await handleFileUpload(file);
    } catch {
      setError('Failed to load sample file. Please try again.');
      setIsUploading(false);
      setSelectedFile(null);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!isAiUploadAllowed(file)) {
      setError(AI_UPLOAD_ERROR);
      setSelectedFile(null);
      setUploadSuccess(false);
      setIsUploading(false);
      return;
    }

    try {
      beginNewUpload();
      setSelectedFile(file);
      setIsUploading(true);

      const uploadResult = await apiClient.documents.uploadFile({
        file,
        type: UploadType.NewFile,
        ...ONBOARDING_SHOWCASE_UPLOAD_OPTIONS,
        ...(contactNumericId != null ? { contact_id: contactNumericId } : {}),
        ...(!accountStepSkipped && accountNumericId != null
          ? { account_id: accountNumericId }
          : {}),
      });

      const fileId = uploadResult.data.data.id;

      // The bare `POST /api/v3/documents` upload sets only the legacy
      // `documents.contact_id`/`account_id` columns — enough for the activity
      // timeline, but the contact/account Files tab queries through
      // `document_recipients`. Associate the document with the contact (and
      // account, when present) so it appears on those Files pages too. Mirrors
      // the normal firm file-share path; `send_message: false` keeps it silent
      // and `is_private_to_firm: true` keeps it off the client portal.
      let contactAssociationSucceeded = false;
      let accountAssociationSucceeded = false;
      if (contactNumericId != null) {
        const accountId =
          !accountStepSkipped && accountNumericId != null
            ? accountNumericId
            : null;
        try {
          const associationResult =
            await apiClient.documents.associateUploadedDocuments({
              doc_ids: [fileId],
              recipients: [[contactNumericId, accountId, 'contact']],
              is_private_to_firm: true,
            });
          if (associationResult?.success === true) {
            contactAssociationSucceeded = true;
            accountAssociationSucceeded = accountId != null;
          } else {
            setError(
              associationResult?.message ||
                'File uploaded, but recipient association failed.'
            );
          }
        } catch {
          setError('File uploaded, but recipient association failed.');
        }
      }

      setUploadSuccess(true);
      setHasUploadedFile(true, file.name, fileId);
      setUploadedFileId(fileId);
      setAiIngestionStatus(AiIngestionStatus.pending);
      setIsUploading(false);

      trackEvent('file_uploaded', {
        source: 'onboarding',
        user_role: 'firm_employee',
        company_name: homeData?.data?.userpilot_data?.firm_name,
        onboarding_step: 'file_uploaded',
      });
      void recordOnboardingJourneyStep({
        stageKey: ONBOARDING_STAGE_KEYS.fileUploaded,
        status: 'completed',
        metadata: {
          doc_id: fileId,
          has_contact_association: contactAssociationSucceeded,
          has_account_association: accountAssociationSucceeded,
        },
      });
    } catch {
      setError('Failed to upload file. Please try again.');
      setIsUploading(false);
      setUploadSuccess(false);
      setSelectedFile(null);
    }
  };

  const isReadingDocument =
    Boolean(selectedFile) &&
    (isUploading || aiIngestionStatus === AiIngestionStatus.pending);

  useEffect(() => {
    return () => {
      setHasUploadedFile(false);
      setAiIngestionStatus(AiIngestionStatus.pending);
      setUploadedFileId(null);
      setHasNameSuggestion(null);
    };
  }, [setHasUploadedFile, setAiIngestionStatus]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Icon icon="sparkles" sx={{ color: 'primary.main', fontSize: 24 }} />
        <Typography variant="h4" fontWeight={700}>
          Try file intelligence with your new client
        </Typography>
      </Stack>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 2, maxWidth: 640 }}
      >
        Upload a document and watch Liscio identify it, name it, and route it to
        the right client automatically. This is just a demo — nothing is shared
        with your client.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FileAssociationPreview />

      <FileIntelligenceUploadZone
        selectedFile={selectedFile}
        isReading={isReadingDocument}
        disabled={isUploading}
        onFileSelected={handleFileUpload}
      />

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Or try a sample document
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 1,
            borderColor: 'grey.300',
            overflow: 'hidden',
          }}
        >
          {SAMPLE_DOCUMENTS.map((doc, index) => (
            <Box
              key={doc.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 1.5,
                py: 1,
                borderBottom:
                  index < SAMPLE_DOCUMENTS.length - 1 ? '1px solid' : 'none',
                borderColor: 'grey.300',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Icon
                  icon="fileLines"
                  sx={{ color: 'text.secondary', fontSize: 18 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} lineHeight={1.3}>
                    {doc.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    lineHeight={1.3}
                  >
                    {doc.subtitle}
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="text"
                color="primary"
                disabled={isUploading}
                onClick={() =>
                  handleSampleFileClick(doc.publicPath, doc.filename)
                }
                endIcon={<Icon icon="chevronRight" sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  minWidth: 'auto',
                  py: 0.5,
                  px: 1,
                }}
              >
                Use sample
              </Button>
            </Box>
          ))}
        </Paper>
      </Box>

      {uploadSuccess && selectedFile && (
        <Box sx={{ mt: 1.5 }}>
          {aiIngestionStatus === AiIngestionStatus.pending && (
            <Alert severity="info" sx={{ alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body1" fontWeight={600}>
                  Processing file...
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>{selectedFile.name}</strong> is being processed. This
                may take up to 10 seconds.
              </Typography>
            </Alert>
          )}

          {aiIngestionStatus === AiIngestionStatus.completed &&
            hasNameSuggestion === true && (
              <Alert severity="success" sx={{ alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon icon="check" sx={{ fontSize: 20 }} />
                  <Typography variant="body1" fontWeight={600}>
                    File processed successfully!
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{selectedFile.name}</strong> has been processed. Click
                  &quot;Complete setup&quot; to see file naming and tagging in
                  action.
                </Typography>
              </Alert>
            )}

          {aiIngestionStatus === AiIngestionStatus.completed &&
            hasNameSuggestion === false && (
              <Alert severity="warning" sx={{ alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon icon="warning" sx={{ fontSize: 20 }} />
                  <Typography variant="body1" fontWeight={600}>
                    No file name to suggest for this one
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{selectedFile.name}</strong> doesn&apos;t look like a
                  supported document, so there&apos;s nothing to rename. Try a
                  tax return, W-2, 1099, bank statement, invoice, or K-1 — or
                  one of the samples above.
                </Typography>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={resetUploadState}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  Try another file
                </Button>
              </Alert>
            )}

          {aiIngestionStatus === AiIngestionStatus.failed && (
            <Alert severity="error" sx={{ alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon icon="warning" sx={{ fontSize: 20 }} />
                <Typography variant="body1" fontWeight={600}>
                  Processing failed
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>{selectedFile.name}</strong> could not be analyzed.
                Upload a PDF, PNG, or JPEG under 100 MB to continue with setup.
              </Typography>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={resetUploadState}
                sx={{ mt: 2, textTransform: 'none' }}
              >
                Try another file
              </Button>
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FileIntelligenceView;
